import t from "@/lang/lang";
import {
  BaseDataPage,
  BaseDataPageContent,
  BaseDataPageHeader,
  BaseDataPageTitle,
} from "@/routes/base-data/_components/layout/base-data-page";
import { PestcontrolMonitorEditor } from "@/services/backend/pestcontrol/components/monitor-editor";
import { PestcontrolMonitorTable } from "@/services/backend/pestcontrol/components/monitors-table";
import { usePestcontrolMonitorShowQuery } from "@/services/backend/pestcontrol/api/monitor-api";
import { PestcontrolMonitorListRequest } from "@/services/backend/pestcontrol/types/monitor";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Link, Outlet, useNavigate, useParams } from "react-router";
import { useMemo, useState } from "react";

export function PestcontrolMonitorsRoute() {
  const { monitorId } = useParams();
  const basePath = "/base-data/pestcontrol/monitors";
  const { data: monitor } = usePestcontrolMonitorShowQuery(
    { id: monitorId! },
    { skip: !monitorId },
  );

  return (
    <BaseDataPage>
      <BaseDataPageHeader>
        <BaseDataPageTitle>{t("Schädlingsbekämpfung")}</BaseDataPageTitle>
      </BaseDataPageHeader>
      <BaseDataPageContent>
        <Card>
          {monitorId && monitor && (
            <CardContent>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={basePath}>{t("Monitorübersicht")}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      {monitor.name || t("Monitor")}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </CardContent>
          )}
          <Outlet />
        </Card>
      </BaseDataPageContent>
    </BaseDataPage>
  );
}

export function PestcontrolMonitorsRoutePage() {
  const navigate = useNavigate();
  const { companyId } = useActiveCompany();
  const [request, setRequest] = useState<PestcontrolMonitorListRequest>({
    company: { active: true, values: [companyId] },
    orderBy: [{ field: "name" }],
    limit: 500,
  });
  const effectiveRequest = useMemo(
    () => ({
      ...request,
      company: { active: true, values: [companyId] },
    }),
    [companyId, request],
  );

  return (
    <CardContent>
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{t("Monitore")}</h2>
          <p className="text-muted-foreground text-sm">
            {t(
              "Übersicht aller Basis-Monitore. Sie können neue Monitore anlegen oder bestehende bearbeiten.",
            )}
          </p>
        </div>
        <PestcontrolMonitorTable
          companyId={companyId}
          request={effectiveRequest}
          onRequestChange={setRequest}
          onEditMonitor={(id) => navigate(id)}
        />
      </div>
    </CardContent>
  );
}

export function PestcontrolMonitorEditorRoute() {
  const navigate = useNavigate();
  const { monitorId } = useParams();
  const { companyId } = useActiveCompany();

  if (!monitorId) {
    return null;
  }

  return (
    <PestcontrolMonitorEditor
      monitorId={monitorId}
      companyId={companyId}
      onNavigateMonitor={(id) => navigate(`../${id}`, { replace: true })}
      onCancel={() => navigate("/base-data/pestcontrol/monitors")}
      onDeleted={() =>
        navigate("/base-data/pestcontrol/monitors", { replace: true })
      }
    />
  );
}
